import { useMemo } from 'react';

import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import RichText from '@eneco/components/RichText/RichText';
import {
  CurrentAdvanceAmountVsRecommendedAdvanceAmountCardResponse,
  postEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierAdvanceAmounts,
} from '@eneco/dc-be/client';
import { useFormatter } from '@eneco/i18n';
import { Heading, Stack, Text, TextLink } from '@eneco/sparky';
import { CircleCheckmark } from '@eneco/sparky/illustrations';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { DashboardOverviewRendering } from '@sitecore/types/DashboardOverview';
import { useSWRConfig } from 'swr';

import { StyledDashboardBlock } from './DashboardBlock';
import { DashboardLink } from './DashboardLink';
import AlignYBaseline from '../../../../components/AlignYBaseLine';
import BorderedBox from '../../../../components/BorderedBox';
import ReconcileAdvanceAmountDisclaimer from '../../../../components/ReconcileAdvanceAmountDisclaimer';
import { useAddressOptions } from '../../../../hooks/useAddressOptions';
import { useRedirectAndNotifyBE } from '../../../../hooks/useRedirectAndNotifyBE';
import { addressSearchParam } from '../../../../utils/addSearchParams';
import { isPaymentInMeterReadingMonth } from '../../advance/hooks/useAdvances';
import { calculateReconcileGlobalWithExistingCurrentAmounts } from '../../advance/utils/calculateReconcileAdvanceAmount';
import { canEditAdvanceAmount } from '../../advance/utils/canEditAdvances';
import { filteredAdvancesWithActiveContractAndCurrentAmount } from '../../advance/utils/filterActiveAdvances';

export const CurrentAdvanceAmountVsRecommendedAdvanceAmountCard = ({
  card,
}: {
  card: CurrentAdvanceAmountVsRecommendedAdvanceAmountCardResponse;
}) => {
  const { fields } = useContent<DashboardOverviewRendering>();
  const {
    selectedAccount: { crmAccountNumber: accountNumber },
    isCurrentAccountReader,
  } = useSelfServiceAccount();
  const { details } = card;
  const { advances } = details;
  const currentAmount = advances?.totals?.currentAmount ?? 0;
  const recommendedAmount = advances?.totals?.recommendedAmount; // is null when cache is not primed yet
  const { currency } = useFormatter();
  const isPayingRecommendedAmount = recommendedAmount == null || currentAmount === recommendedAmount;
  const { addressIdentifier } = useAddressOptions();
  const { mutate } = useSWRConfig();
  const { trackEventBE } = useTracking();
  const redirectAndNotify = useRedirectAndNotifyBE();

  const activeAdvances = useMemo(
    () =>
      filteredAdvancesWithActiveContractAndCurrentAmount({
        advances,
      }),
    [advances],
  );

  const reconcileAdvanceAmount = calculateReconcileGlobalWithExistingCurrentAmounts({
    advances: activeAdvances,
  });

  const submitGlobalRecommendedAdvance = async ({ amount }: { amount: number | null | undefined }) => {
    if (amount == null) return;

    try {
      const { response } =
        await postEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierAdvanceAmounts({
          path: { accountNumber, addressIdentifier },
          body: { amount, type: 'Global' },
        });
      if (!response.ok) {
        throw new Error('request failed');
      }
      trackEventBE('be_bbp_advice', { status: 'success' });
    } catch (e) {
      void e;
      trackEventBE('be_bbp_advice', { status: 'failed' });
      throw new Error('request failed');
    }

    const advanceCacheKey = `/accounts/${accountNumber}/delivery-addresses/${addressIdentifier}/advance-amounts`;
    await mutate(advanceCacheKey);

    redirectAndNotify({
      route: fields.currentAdvanceAmountVsRecommended.acceptRecommendedAmountLink.value.href,
      title: fields.currentAdvanceAmountVsRecommended?.successNotification?.value?.title,
      text: <RichText html={fields.currentAdvanceAmountVsRecommended?.successNotification?.value?.content} />,
      variant: fields.currentAdvanceAmountVsRecommended?.successNotification?.value?.variant,
    });
  };

  const canEdit = canEditAdvanceAmount({
    isCurrentAccountReader,
    advancePayments: activeAdvances?.advancePayments ?? [],
  });

  const isInMeterReadingMonth = activeAdvances?.advancePayments?.some(isPaymentInMeterReadingMonth);
  const showRecommendedAmount = !isPayingRecommendedAmount && recommendedAmount != null && !isInMeterReadingMonth;
  const showAcceptRecommendedAmountLink = showRecommendedAmount && canEdit;

  return (
    <StyledDashboardBlock>
      <Stack gap="4" direction="row" alignY="center">
        <Stack.Item shrink={false}>
          <CircleCheckmark color="feedbackSuccess" size="large" />
        </Stack.Item>
        <Stack gap="2">
          <Text weight="bold">
            <RichText html={fields.currentAdvanceAmountVsRecommended?.description?.value} />
          </Text>
          <AlignYBaseline>
            <Heading as="h3" size={currency.euro(currentAmount).length >= 6 ? 'S' : 'M'}>
              {Math.round(currentAmount) === currentAmount
                ? currency.euroNoFractionDigits(currentAmount)
                : currency.euro(currentAmount)}
            </Heading>
            <Text weight="bold">{fields.currentAdvanceAmountVsRecommended?.eachMonthLabel?.value}</Text>
          </AlignYBaseline>
        </Stack>
      </Stack>
      {showRecommendedAmount && (
        <BorderedBox>
          <Stack gap="4">
            <Text weight="bold">{fields.currentAdvanceAmountVsRecommended?.recommendedAmountTitle?.value}</Text>
            <AlignYBaseline wrap>
              <Heading as="h3" size="M">
                {Math.round(recommendedAmount) === recommendedAmount
                  ? currency.euroNoFractionDigits(recommendedAmount)
                  : currency.euro(recommendedAmount)}
              </Heading>
              <Text weight="bold">{fields.currentAdvanceAmountVsRecommended?.eachMonthLabel?.value}</Text>
            </AlignYBaseline>
          </Stack>
          <ReconcileAdvanceAmountDisclaimer
            amount={reconcileAdvanceAmount}
            recommendedAmountTooLowDisclaimerLabel={
              fields.currentAdvanceAmountVsRecommended?.recommendedAmountTooLowDisclaimerLabel?.value
            }
            recommendedAmountTooHighDisclaimerLabel={
              fields.currentAdvanceAmountVsRecommended?.recommendedAmountTooHighDisclaimerLabel?.value
            }
          />
        </BorderedBox>
      )}
      {showAcceptRecommendedAmountLink && (
        <DashboardLink
          emphasis="high"
          onClick={async () => {
            await submitGlobalRecommendedAdvance({ amount: recommendedAmount });
          }}
          trackingComponentName="current_advance_vs_recommended_accept_amount"
          trackingComponentType="dashboard_link">
          {fields.currentAdvanceAmountVsRecommended?.acceptRecommendedAmountLink?.value?.text ||
            'Accept recommended amount [Not in Sitecore]'}
        </DashboardLink>
      )}
      <TextLink
        href={fields.currentAdvanceAmountVsRecommended?.link.value.href + addressSearchParam(addressIdentifier)}
        emphasis="high">
        {fields.currentAdvanceAmountVsRecommended?.link.value.text || 'Advance payments [Not in Sitecore]'}
      </TextLink>
    </StyledDashboardBlock>
  );
};
